import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { Icon } from '@progress/kendo-react-common';
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { FilterDescriptor } from '@progress/kendo-react-dropdowns/dist/npm/common/filterDescriptor';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularIconButton from 'src/components/design-system/CircularIconButton';
import { FavoriteFolder } from 'src/types/favorite';
import { useContextSelector } from 'use-context-selector';
import { ShoppingListProductsContext } from './ShoppingListProductsContext';

const ShoppingListDropdown = () => {
  const { t } = useTranslation();
  const shoppingList = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.shoppingList
  );
  const shoppingListFolders = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.shoppingListFolders ?? []
  );
  const selectShoppingList = useContextSelector(
    ShoppingListProductsContext,
    (context) => context.selectShoppingList
  );
  const [data, setData] = useState<FavoriteFolder[]>(shoppingListFolders);

  useEffect(() => {
    setData(shoppingListFolders);
  }, [shoppingListFolders]);

  const filterData = useCallback(
    (filter: FilterDescriptor | CompositeFilterDescriptor) =>
      filterBy([...shoppingListFolders], filter),
    [shoppingListFolders]
  );

  const filterChange = useCallback(
    (event: DropDownListFilterChangeEvent) => {
      setData(filterData(event.filter));
    },
    [filterData]
  );

  const handleShoppingListChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { value } = e.target;
      selectShoppingList(value);
    },
    [selectShoppingList]
  );

  const dropDownIndexByFolder = useMemo(
    () => shoppingListFolders.findIndex((folder) => folder.id === shoppingList?.id),
    [shoppingListFolders, shoppingList?.id]
  );

  const isLeftBtnDropDwnDisabled = dropDownIndexByFolder - 1 < 0;
  const isRightBtnDropDwnDisabled = dropDownIndexByFolder + 1 >= shoppingListFolders.length;

  const onLeftBtnDropDwnClick = useCallback(() => {
    const previousFolder = shoppingListFolders[dropDownIndexByFolder - 1];
    if (!previousFolder) {
      return;
    }
    selectShoppingList(previousFolder);
  }, [dropDownIndexByFolder, selectShoppingList, shoppingListFolders]);

  const onRightBtnDropDwnClick = useCallback(() => {
    const nextFolder = shoppingListFolders[dropDownIndexByFolder + 1];
    if (!nextFolder) {
      return;
    }
    selectShoppingList(nextFolder);
  }, [dropDownIndexByFolder, selectShoppingList, shoppingListFolders]);

  const listNoDataRender = useCallback(
    () => (
      <div className="p-4 text-center text-gray-500 text-sm">
        <Icon name="warning" className="text-2xl mb-2" />
        <p>{t('NO DATA FOUND.')}</p>
      </div>
    ),
    [t]
  );

  return (
    <div className="min-w-0 max-w-full">
      <div className="mt-1 flex min-w-0 max-w-full items-center gap-2">
        <span
          id="arrow-end-left-anchor"
          className="shrink-0"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={
            isLeftBtnDropDwnDisabled ? t('No Previous Shopping List') : t('Previous Shopping List')
          }
        >
          <CircularIconButton
            icon="arrow-end-left"
            disabled={isLeftBtnDropDwnDisabled}
            onClick={onLeftBtnDropDwnClick}
          />
        </span>
        <div className="h-fit w-[250px] min-w-0 max-w-full shrink">
          <DropDownList
            id="view-drop-down-list"
            className="shopping-list-folder-dropdown"
            data={data}
            onChange={handleShoppingListChange}
            value={shoppingList}
            textField="description"
            dataItemKey="id"
            style={{ width: '100%' }}
            filterable
            onFilterChange={filterChange}
            listNoDataRender={listNoDataRender}
          />
        </div>
        <span
          id="arrow-end-right-anchor"
          className="shrink-0"
          data-tooltip-id="global-tooltip"
          data-tooltip-content={
            isRightBtnDropDwnDisabled ? t('No Next Shopping List') : t('Next Shopping List')
          }
        >
          <CircularIconButton
            icon="arrow-end-right"
            disabled={isRightBtnDropDwnDisabled}
            onClick={onRightBtnDropDwnClick}
          />
        </span>
      </div>
    </div>
  );
};

export default ShoppingListDropdown;
